import React, { useMemo } from 'react';
import { Box, Typography, useTheme, useMediaQuery } from '@mui/material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { StudyPlanData } from '../UserAndProfile/UserContext';
import { getCurrentWeek } from './studyPlanUtils';

interface WeeklyProgressChartProps {
  studyPlan: StudyPlanData;
}

const WeeklyProgressChart: React.FC<WeeklyProgressChartProps> = ({ studyPlan }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const currentWeek = getCurrentWeek(studyPlan, new Date());

  const chartData = useMemo(() =>
    studyPlan.weeklyPlans.map(week => ({
      week: `W${week.weekNumber}`,
      weekNumber: week.weekNumber,
      questions: week.questionsAttempted || 0,
      accuracy: week.questionsAttempted
        ? Math.round(((week.questionsCorrect || 0) / week.questionsAttempted) * 100)
        : 0,
      daysActive: week.activeDates?.length || 0,
      isCurrent: week.weekNumber === currentWeek?.weekNumber,
    })),
    [studyPlan.weeklyPlans, currentWeek?.weekNumber]
  );

  const hasData = chartData.some(d => d.questions > 0);

  if (!hasData) {
    return (
      <Box sx={{ py: 3, px: { xs: 1, sm: 2 }, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Complete some practice to see your progress over time.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', height: { xs: 220, sm: 260 }, px: { xs: 0, sm: 1 }, py: 1 }}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={chartData} margin={{ top: 5, right: isMobile ? 5 : 20, left: isMobile ? -10 : 0, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
          <XAxis dataKey="week" tick={{ fontSize: isMobile ? 10 : 12 }} />
          <YAxis yAxisId="left" tick={{ fontSize: isMobile ? 10 : 12 }} width={isMobile ? 28 : 36} />
          <YAxis yAxisId="right" orientation="right" domain={[0, 100]} tick={{ fontSize: isMobile ? 10 : 12 }} width={isMobile ? 28 : 36} />
          <Tooltip
            contentStyle={{ fontSize: isMobile ? 12 : 14 }}
            formatter={(value: number, name: string) => {
              if (name === 'accuracy') return [`${value}%`, 'Accuracy'];
              if (name === 'daysActive') return [value, 'Days active'];
              return [value, 'Questions'];
            }}
          />
          {!isMobile && <Legend wrapperStyle={{ fontSize: 12 }} />}
          <Line yAxisId="left" type="monotone" dataKey="questions" stroke={theme.palette.primary.main} strokeWidth={2} dot={{ r: 3 }} name="Questions" />
          <Line yAxisId="right" type="monotone" dataKey="accuracy" stroke={theme.palette.secondary.main} strokeWidth={2} dot={{ r: 3 }} name="Accuracy" />
          <Line yAxisId="left" type="monotone" dataKey="daysActive" stroke={theme.palette.success.main} strokeWidth={2} dot={{ r: 3 }} name="Days active" />
        </LineChart>
      </ResponsiveContainer>
    </Box>
  );
};

export default WeeklyProgressChart;
